import ErrorIcon from '@mui/icons-material/Error'
import React from 'react'
import { createPortal } from 'react-dom'
import { ResponsiveContainer } from 'recharts'
import styled from 'styled-components'
import useResizeObserver from 'use-resize-observer'

import { EmptyState } from 'components/empty-state/EmptyState'
import {
  HOVER_TRANSITION_CSS,
  HOVER_TRANSITION_MS,
  HOVER_TRANSLUCENT_DIMMED_OPACITY,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import type { ChartHoverAnnotationRow } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { getSentimentBarColor } from 'features/charts-v2/constants'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import type {
  SankeyGraph,
  SankeyLinkDatum,
  SankeyNodeDatum,
} from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import {
  SankeyDiagram,
  type SankeyColors,
  type SankeyHandlers,
  type SankeyLabelLayout,
} from 'features/charts-v2/strategies/SankeyDiagram'
import type { ChartProps } from 'features/charts-v2/types/chart-strategy'
import type { FlowColorMode } from 'features/charts-v2/types/filter-hash/v2'
import { placeTooltip } from 'features/charts-v2/utils/tooltip-placement'

/** Below this container height the nodes cannot be laid out legibly. */
export const SANKEY_MIN_HEIGHT_PX = 160

const NEUTRAL_ORIGIN_COLOR = '#bfbfbf'
const TOOLTIP_OFFSET_PX = 14
const TOOLTIP_VIEWPORT_MARGIN_PX = 8

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

// Fixed in the viewport and portalled to the body, so neither the chart pane's
// overflow nor a transformed dashboard-tile ancestor can clip or offset it. The
// real position is written in SankeyTooltip once its size is known.
const TooltipAnchor = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  pointer-events: none;
`

/** Everything the chart takes from the panel's display settings. */
export interface SankeyDisplay {
  flowColorMode: FlowColorMode
  fontSizePx: number
  showNodeCounts: boolean
  /** What a flow's size counts, e.g. "Mentions". */
  volumeLabel: string
  /** One heading per column, in flow order. */
  columnLabels: string[]
  commonMargin: ReturnType<BaseChartStrategy['getCommonMargin']>
}

interface SankeyFrameProps {
  graph: SankeyGraph
  display: SankeyDisplay
  originColors: Map<string, string>
  handlers: SankeyHandlers
}

// Memoised so a hover (which only moves the tooltip / dims flows) never
// re-lays-out Recharts' Sankey.
const SankeyFrame = React.memo<SankeyFrameProps>(
  ({ graph, display, originColors, handlers }) => {
    const labels: SankeyLabelLayout = {
      fontSizePx: display.fontSizePx,
      showCounts: display.showNodeCounts,
    }
    const colors: SankeyColors = {
      originColors,
      flowColorMode: display.flowColorMode,
    }
    return (
      <ResponsiveContainer width="100%" height="100%" debounce={100}>
        <SankeyDiagram
          graph={graph}
          columnLabels={display.columnLabels}
          commonMargin={display.commonMargin}
          colors={colors}
          labels={labels}
          handlers={handlers}
        />
      </ResponsiveContainer>
    )
  },
)

SankeyFrame.displayName = 'SankeyFrame'

/** First-column nodes take the breakdown colour map; group nodes (whose
 *  labels the map does not hold) and overflow take the palette by position. */
const buildOriginColors = (
  graph: SankeyGraph,
  colorMap: Map<string, string> | undefined,
): Map<string, string> => {
  const colors = new Map<string, string>()
  graph.nodes
    .filter((node) => node.column === 0)
    .forEach((node, index) => {
      const mapped =
        node.wireValue !== null && node.level === 'value'
          ? colorMap?.get(node.wireValue)
          : undefined
      colors.set(
        node.id,
        node.isOther
          ? NEUTRAL_ORIGIN_COLOR
          : (mapped ?? chartConfig.getColor('primary', index)),
      )
    })
  return colors
}

interface SankeyHover {
  linkIndex: number
  /** Pointer position in viewport coordinates. */
  clientX: number
  clientY: number
}

const buildTooltipRows = (
  link: SankeyLinkDatum,
  source: SankeyNodeDatum,
  volumeLabel: string,
): ChartHoverAnnotationRow[] => {
  const rows: ChartHoverAnnotationRow[] = [
    {
      label: volumeLabel,
      value: link.count.toLocaleString(),
      percentage: null,
      color: null,
    },
    {
      label: `Share of ${source.label}`,
      value: `${((link.count / source.total) * 100).toFixed(1)}%`,
      percentage: null,
      color: null,
    },
  ]
  if (link.avg !== null) {
    rows.push({
      label: 'Average sentiment',
      value: link.avg.toFixed(2),
      percentage: null,
      color: getSentimentBarColor(link.avg),
    })
  }
  return rows
}

interface SankeyTooltipProps {
  link: SankeyLinkDatum
  source: SankeyNodeDatum
  target: SankeyNodeDatum
  hover: SankeyHover
  volumeLabel: string
}

const SankeyTooltip: React.FC<SankeyTooltipProps> = ({
  link,
  source,
  target,
  hover,
  volumeLabel,
}) => {
  const anchorRef = React.useRef<HTMLDivElement>(null)

  // Place from the tooltip's real size. A layout effect runs before paint, so
  // the tooltip never shows at a stale position, and writing the style directly
  // avoids a second render per pointer move.
  React.useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const { left, top } = placeTooltip({
      cursor: { x: hover.clientX, y: hover.clientY },
      size: { width: anchor.offsetWidth, height: anchor.offsetHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      offset: TOOLTIP_OFFSET_PX,
      margin: TOOLTIP_VIEWPORT_MARGIN_PX,
    })
    anchor.style.left = `${left}px`
    anchor.style.top = `${top}px`
  }, [hover])

  return createPortal(
    <TooltipAnchor ref={anchorRef}>
      <ChartDatapointHoverAnnotation
        title={`${source.label} → ${target.label}`}
        rows={buildTooltipRows(link, source, volumeLabel)}
        footer={null}
        focusedField={null}
      />
    </TooltipAnchor>,
    document.body,
  )
}

/** What the pointer is on: a node (its ribbons stay lit) or one ribbon (that
 *  ribbon darkens), or nothing. */
type FlowFocus =
  | { kind: 'node'; nodeId: string }
  | { kind: 'link'; source: string; target: string }
  | null

// A ribbon rests at the LINK_OPACITY stroke opacity drawn by SankeyDiagram; the
// one under the pointer darkens to this.
const LINK_HOVER_STROKE_OPACITY = 0.8

/** Dim every ribbon that is not lit by the focus, and darken a focused ribbon
 *  (no focus: restore). Imperative on purpose, so a hover never re-renders the
 *  memoised Recharts frame. */
const applyFlowHighlight = (root: HTMLElement, focus: FlowFocus): void => {
  root.querySelectorAll<SVGElement>('[data-sankey-source]').forEach((flow) => {
    if (focus === null) {
      flow.style.opacity = ''
      flow.style.strokeOpacity = ''
      return
    }
    const source = flow.getAttribute('data-sankey-source')
    const target = flow.getAttribute('data-sankey-target')
    const lit =
      focus.kind === 'node'
        ? source === focus.nodeId || target === focus.nodeId
        : source === focus.source && target === focus.target
    flow.style.transition = `${HOVER_TRANSITION_CSS}, stroke-opacity ${HOVER_TRANSITION_MS}ms ease`
    flow.style.opacity = lit ? '' : String(HOVER_TRANSLUCENT_DIMMED_OPACITY)
    flow.style.strokeOpacity =
      focus.kind === 'link' && lit ? String(LINK_HOVER_STROKE_OPACITY) : ''
  })
}

interface SankeyChartViewProps {
  graph: SankeyGraph
  display: SankeyDisplay
  colorMap?: Map<string, string>
  onSegmentClick?: ChartProps['onSegmentClick']
}

const SankeyChartViewInner: React.FC<SankeyChartViewProps> = ({
  graph,
  display,
  colorMap,
  onSegmentClick,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { height } = useResizeObserver({ ref: containerRef })
  const [hover, setHover] = React.useState<SankeyHover | null>(null)
  const [hoveredNodeId, setHoveredNodeId] = React.useState<string | null>(null)

  const originColors = React.useMemo(
    () => buildOriginColors(graph, colorMap),
    [graph, colorMap],
  )
  const nodesById = React.useMemo(
    () => new Map(graph.nodes.map((node) => [node.id, node])),
    [graph],
  )

  // The tooltip's ribbon is the hovered one. A primitive, so moving over the
  // same ribbon (which updates `hover` on every pointer move) does not re-run
  // the highlight.
  const hoveredLinkIndex = hover?.linkIndex ?? null

  React.useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const link =
      hoveredLinkIndex === null ? undefined : graph.links[hoveredLinkIndex]
    const focus: FlowFocus =
      hoveredNodeId !== null
        ? { kind: 'node', nodeId: hoveredNodeId }
        : link
          ? { kind: 'link', source: link.source, target: link.target }
          : null
    applyFlowHighlight(root, focus)
  }, [hoveredNodeId, hoveredLinkIndex, graph])

  const handlers = React.useMemo<SankeyHandlers>(() => {
    // "Other" stands for many values, so it contributes no filter; a click that
    // names nothing else has nothing to summarize.
    const emit = (
      source: SankeyNodeDatum,
      target: SankeyNodeDatum | null,
      count: number,
      event: React.MouseEvent,
    ): void => {
      if (!onSegmentClick) return
      if (source.isOther && (target === null || target.isOther)) return
      onSegmentClick(
        {
          primaryBreakdown: source.wireValue ?? source.label,
          stackingBreakdown: target
            ? (target.wireValue ?? target.label)
            : undefined,
          value: count,
          payload: {
            sankeySourceBreakdown: source.breakdownIndex,
            sankeySourceLevel: source.level,
            sankeySourceValue: source.wireValue,
            sankeyTargetBreakdown: target ? target.breakdownIndex : null,
            sankeyTargetLevel: target ? target.level : null,
            sankeyTargetValue: target ? target.wireValue : null,
          },
        },
        event,
      )
    }
    return {
      onLinkHover: (linkIndex, clientX, clientY) =>
        setHover({ linkIndex, clientX, clientY }),
      onLinkLeave: () => setHover(null),
      onLinkClick: (linkIndex, event) => {
        const link = graph.links[linkIndex]
        const source = link && nodesById.get(link.source)
        const target = link && nodesById.get(link.target)
        if (link && source && target) emit(source, target, link.count, event)
      },
      onNodeHover: setHoveredNodeId,
      onNodeClick: (nodeId, event) => {
        const node = nodesById.get(nodeId)
        if (node) emit(node, null, node.total, event)
      },
    }
  }, [graph, nodesById, onSegmentClick])

  if (height !== undefined && height < SANKEY_MIN_HEIGHT_PX) {
    return (
      <ChartContainer ref={containerRef}>
        <EmptyState
          icon={<ErrorIcon fontSize="large" />}
          title="Needs more space"
          description="Make this chart taller to see the flow."
          fullHeight="container"
          textWidth="300px"
        />
      </ChartContainer>
    )
  }

  const hoveredLink = hover ? graph.links[hover.linkIndex] : undefined
  const hoveredSource = hoveredLink && nodesById.get(hoveredLink.source)
  const hoveredTarget = hoveredLink && nodesById.get(hoveredLink.target)

  return (
    <ChartContainer ref={containerRef}>
      <SankeyFrame
        graph={graph}
        display={display}
        originColors={originColors}
        handlers={handlers}
      />
      {hover && hoveredLink && hoveredSource && hoveredTarget && (
        <SankeyTooltip
          link={hoveredLink}
          source={hoveredSource}
          target={hoveredTarget}
          hover={hover}
          volumeLabel={display.volumeLabel}
        />
      )}
    </ChartContainer>
  )
}

export const SankeyChartView = React.memo(SankeyChartViewInner)
